import React, { useState, useEffect } from "react";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import {
  CheckCircle,
  Heart,
  MessageSquare,
  Shield,
  Users,
  Star,
  Clock,
  Coffee,
  Book,
  Zap,
  AlertCircle,
} from "lucide-react";
import { Helmet } from "react-helmet";
const HealthyRelationshipSigns = () => {
  const [activeSection, setActiveSection] = useState("signs");

  // Push ads to AdSense queue after component mounts
  useEffect(() => {
    document.title = "Healthy Relationships";
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (e) {
      console.error("AdSense error:", e);
    }
  }, []);

  // Example relationship scenarios
  const scenarios = [
    {
      title: "Communication Example",
      healthy:
        '"I feel anxious when plans change last minute because it disrupts my routine. Could we try to give each other more notice when possible?"',
      unhealthy:
        '"You always do this! You never consider how your actions affect me. You\'re so selfish."',
    },
    {
      title: "Conflict Resolution Example",
      healthy:
        "\"I understand you're frustrated about the chores. Let's come up with a system that works better for both of us.\"",
      unhealthy:
        "\"You're always nagging me about cleaning. Why don't you just do it yourself if it bothers you so much?\"",
    },
    {
      title: "Boundary Example",
      healthy:
        '"I need some alone time this weekend to recharge. It\'s not about you—I just need space occasionally."',
      unhealthy:
        '"Why are you always trying to control what I do? I\'ll do whatever I want without checking with you."',
    },
    {
      title: "Supporting Growth Example",
      healthy:
        '"I\'m excited about your new opportunity! What can I do to support you while you pursue this?"',
      unhealthy:
        "\"Why would you want to do that? It's going to take time away from our relationship and probably won't work out anyway.\"",
    },
  ];

  // Red flags in relationships
  const redFlags = [
    {
      title: "Controlling Behavior",
      description:
        "Your partner monitors your whereabouts, controls your finances, isolates you from loved ones, or makes decisions for you without your input.",
    },
    {
      title: "Consistent Disrespect",
      description:
        "Your partner regularly belittles your opinions, mocks your interests, or dismisses your feelings and concerns.",
    },
    {
      title: "Jealousy and Possessiveness",
      description:
        "Your partner displays excessive jealousy, accuses you of cheating without reason, or tries to control who you can see or talk to.",
    },
    {
      title: "Lack of Support",
      description:
        "Your partner shows little interest in your goals, dismisses your achievements, or actively discourages your personal growth.",
    },
    {
      title: "Gaslighting",
      description:
        "Your partner denies or twists reality, making you question your own perceptions, memories, or sanity.",
    },
    {
      title: "Repeated Boundary Violations",
      description:
        "Your partner consistently ignores or pushes against your stated boundaries, showing little respect for your comfort or autonomy.",
    },
  ];

  // Expanded relationship sign details
  const signDetails = [
    {
      title: "Open Communication",
      icon: <MessageSquare size={24} />,
      description:
        "In healthy relationships, partners can express thoughts, feelings, needs, and concerns honestly without fear of judgment or retaliation. Communication isn't just about talking, but also active listening and understanding.",
      indicators: [
        "You discuss difficult topics respectfully",
        "You listen to understand, not just to respond",
        "Conversations flow easily about both light and serious topics",
        "You feel heard and understood by your partner",
        "Non-verbal communication (body language, tone) matches verbal messages",
      ],
    },
    {
      title: "Mutual Respect",
      icon: <Users size={24} />,
      description:
        "Respect is the foundation of any healthy relationship. It means valuing each other's opinions, boundaries, and individuality even when you disagree. Partners in respectful relationships treat each other with kindness and consideration.",
      indicators: [
        "You value each other's perspectives even when different",
        "You speak to each other with kindness, even during disagreements",
        "You consider each other's needs when making decisions",
        "You acknowledge each other's accomplishments",
        "You respect each other's boundaries without pushback",
      ],
    },
    {
      title: "Trust",
      icon: <Shield size={24} />,
      description:
        "Trust means feeling secure in the relationship without constant worry, jealousy, or suspicion. It's built through consistency, reliability, and honesty over time, creating a foundation of emotional safety.",
      indicators: [
        "You feel secure when you're apart",
        "You keep your promises and commitments to each other",
        "You're honest with each other, even when it's difficult",
        "You can depend on each other in times of need",
        "You don't feel the need to check up on or monitor each other",
      ],
    },
    {
      title: "Support for Growth",
      icon: <Zap size={24} />,
      description:
        "Partners in healthy relationships encourage each other's personal development and celebrate achievements. They want each other to thrive as individuals, not just as a couple.",
      indicators: [
        "You encourage each other to pursue goals and passions",
        "You celebrate each other's successes without jealousy",
        "You offer emotional support during challenges",
        "You inspire each other to become better versions of yourselves",
        "You help each other overcome obstacles to personal growth",
      ],
    },
    {
      title: "Healthy Conflict Resolution",
      icon: <CheckCircle size={24} />,
      description:
        "Conflict is inevitable in relationships, but healthy couples address disagreements respectfully, focusing on understanding and finding solutions rather than winning arguments or assigning blame.",
      indicators: [
        "You focus on the issue at hand, not personal attacks",
        "You both take responsibility for your contributions to problems",
        "You find compromises that work for both of you",
        "You approach disagreements as 'us vs. the problem' not 'me vs. you'",
        "You can agree to disagree on some topics without resentment",
      ],
    },
    {
      title: "Shared Values",
      icon: <Star size={24} />,
      description:
        "While you don't need to be identical, alignment on core values and life goals creates a strong foundation. Understanding what matters most to each other helps navigate life's big decisions together.",
      indicators: [
        "You have similar views on major life decisions (family, finances, etc.)",
        "You respect each other's spiritual or religious beliefs",
        "You share a vision for your future together",
        "Your priorities in life generally align",
        "You have compatible views on work-life balance",
      ],
    },
    {
      title: "Emotional Safety",
      icon: <Heart size={24} />,
      description:
        "Emotional safety means feeling comfortable being your authentic self and expressing vulnerability without fear of ridicule, rejection, or negative consequences.",
      indicators: [
        "You feel comfortable showing vulnerability",
        "You can share your fears and insecurities openly",
        "You don't fear judgment or rejection for expressing emotions",
        "You feel accepted for who you truly are",
        "You don't feel the need to walk on eggshells around your partner",
      ],
    },
    {
      title: "Quality Time",
      icon: <Clock size={24} />,
      description:
        "Healthy couples prioritize spending meaningful time together, being fully present, and nurturing their bond through shared experiences that create lasting memories.",
      indicators: [
        "You regularly make time for each other despite busy schedules",
        "You engage in activities you both enjoy",
        "You're fully present when together (not distracted by devices)",
        "You have meaningful conversations beyond daily logistics",
        "You create rituals and traditions that matter to both of you",
      ],
    },
    {
      title: "Independence",
      icon: <Book size={24} />,
      description:
        "While connection is important, healthy relationships also maintain healthy independence. Each partner has their own identity, interests, and social connections outside the relationship.",
      indicators: [
        "You maintain your own friendships and social circles",
        "You pursue individual hobbies and interests",
        "You respect each other's need for alone time",
        "You can make some decisions independently",
        "Your happiness doesn't depend entirely on your partner",
      ],
    },
    {
      title: "Affection and Appreciation",
      icon: <Coffee size={24} />,
      description:
        "Regular expressions of love, gratitude, and appreciation help partners feel valued and strengthen emotional bonds. This includes both verbal affirmations and physical affection.",
      indicators: [
        "You express gratitude for both big and small things",
        "You show physical affection regularly",
        "You use words of affirmation and encouragement",
        "You notice and acknowledge each other's efforts",
        "You make each other feel loved in ways that resonate individually",
      ],
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-indigo-50 to-white">
      <Header />
      <Helmet>
        <title>Healthy Relationship signs</title>
        <meta
          name="description"
          content="Article talking about healthy relationship signs that can nurture proper reltionship."
        />
      </Helmet>
      {/* Top Banner Ad */}
      <div
        className="container mx-auto px-10 py-6"
        style={{ width: "100%", minWidth: "300px" }}>
        <ins
          className="adsbygoogle"
          style={{ display: "block" }}
          data-ad-client="ca-pub-2067522743161930"
          data-ad-slot="YYYYYYYYYY"
          data-ad-format="auto"
          data-full-width-responsive="true"></ins>
      </div>

      <main className="flex-grow container mx-auto px-10 py-12 max-w-5xl relative">
        {/* Sidebar Ad (visible on medium screens and up) */}
        <div
          className="hidden md:block fixed right-4 top-24 w-48"
          style={{ width: "100%", minWidth: "300px" }}>
          <ins
            className="adsbygoogle"
            style={{ display: "block" }}
            data-ad-client="ca-pub-2067522743161930"
            data-ad-slot="YYYYYYYYYY"
            data-ad-format="auto"
            data-full-width-responsive="true"></ins>
        </div>

        <div className="mb-12 text-center">
          <h1 className="text-5xl font-bold text-indigo-800 mb-6">
            10 Signs of a Healthy Relationship
          </h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Healthy relationships enrich our lives with love, support, and
            growth. They're built on key foundations that create a secure,
            nurturing environment for both partners to thrive.
          </p>
        </div>

        {/* Navigation */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          <button
            onClick={() => setActiveSection("signs")}
            className={`px-6 py-3 rounded-full text-lg font-medium transition-all duration-200 ${
              activeSection === "signs"
                ? "bg-indigo-600 text-white shadow-md"
                : "bg-white text-indigo-600 border border-indigo-200 hover:bg-indigo-50"
            }`}>
            Key Signs
          </button>
          <button
            onClick={() => setActiveSection("examples")}
            className={`px-6 py-3 rounded-full text-lg font-medium transition-all duration-200 ${
              activeSection === "examples"
                ? "bg-indigo-600 text-white shadow-md"
                : "bg-white text-indigo-600 border border-indigo-200 hover:bg-indigo-50"
            }`}>
            Examples
          </button>
          <button
            onClick={() => setActiveSection("redFlags")}
            className={`px-6 py-3 rounded-full text-lg font-medium transition-all duration-200 ${
              activeSection === "redFlags"
                ? "bg-indigo-600 text-white shadow-md"
                : "bg-white text-indigo-600 border border-indigo-200 hover:bg-indigo-50"
            }`}>
            Red Flags
          </button>
          <button
            onClick={() => setActiveSection("building")}
            className={`px-6 py-3 rounded-full text-lg font-medium transition-all duration-200 ${
              activeSection === "building"
                ? "bg-indigo-600 text-white shadow-md"
                : "bg-white text-indigo-600 border border-indigo-200 hover:bg-indigo-50"
            }`}>
            Building Tips
          </button>
        </div>

        {/* Signs Section */}
        {activeSection === "signs" && (
          <div className="space-y-8">
            <p className="text-xl text-gray-700 mb-8">
              While every relationship is unique, these ten signs indicate a
              strong, healthy foundation. They represent the qualities that
              contribute to lasting, fulfilling partnerships.
            </p>

            {signDetails.map((sign, index) => (
              <React.Fragment key={index}>
                <div className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden">
                  <div className="flex flex-col md:flex-row">
                    <div className="bg-indigo-600 text-white p-6 flex justify-center items-center md:w-1/6">
                      <div className="text-center">
                        <div className="flex justify-center mb-2">
                          {sign.icon}
                        </div>
                        <span className="text-2xl font-bold">{index + 1}</span>
                      </div>
                    </div>
                    <div className="p-6 md:p-8 md:w-5/6">
                      <h3 className="text-2xl font-bold text-indigo-800 mb-3">
                        {sign.title}
                      </h3>
                      <p className="text-gray-700 mb-4">{sign.description}</p>
                      <div className="bg-indigo-50 rounded-xl p-4">
                        <h4 className="text-indigo-700 font-semibold mb-2">
                          What it looks like:
                        </h4>
                        <ul className="space-y-1">
                          {sign.indicators.map((indicator, i) => (
                            <li key={i} className="flex items-start">
                              <span className="text-indigo-600 mr-2">•</span>
                              <span className="text-gray-700">{indicator}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                {/* In-Content Ad after fifth sign */}
                {index === 4 && (
                  <div
                    className="my-8"
                    style={{ width: "100%", minWidth: "300px" }}>
                    <ins
                      className="adsbygoogle"
                      style={{ display: "block" }}
                      data-ad-client="ca-pub-2067522743161930"
                      data-ad-slot="YYYYYYYYYY"
                      data-ad-format="auto"
                      data-full-width-responsive="true"></ins>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Examples Section */}
        {activeSection === "examples" && (
          <div className="space-y-8">
            <p className="text-xl text-gray-700 mb-8">
              Understanding the difference between healthy and unhealthy
              relationship patterns can help you recognize strengths and areas
              for improvement in your own relationships.
            </p>

            {scenarios.map((scenario, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-md overflow-hidden">
                <div className="bg-indigo-600 text-white p-4">
                  <h3 className="text-xl font-bold">{scenario.title}</h3>
                </div>
                <div className="grid md:grid-cols-2 gap-4 p-6">
                  <div className="bg-green-50 p-4 rounded-xl border border-green-100">
                    <h4 className="text-lg font-semibold text-green-700 mb-2">
                      Healthy Response:
                    </h4>
                    <p className="text-gray-700 italic">{scenario.healthy}</p>
                    <div className="mt-3 flex items-center text-green-600">
                      <CheckCircle size={16} className="mr-2" />
                      <span className="text-sm font-medium">
                        Focuses on feelings, specific issues, and solutions
                      </span>
                    </div>
                  </div>

                  <div className="bg-red-50 p-4 rounded-xl border border-red-100">
                    <h4 className="text-lg font-semibold text-red-700 mb-2">
                      Unhealthy Response:
                    </h4>
                    <p className="text-gray-700 italic">{scenario.unhealthy}</p>
                    <div className="mt-3 flex items-center text-red-600">
                      <CheckCircle size={16} className="mr-2" />
                      <span className="text-sm font-medium">
                        Uses blame, generalizations, and personal attacks
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <div className="bg-indigo-50 p-8 rounded-2xl border border-indigo-100 mt-12">
              <h3 className="text-2xl font-bold text-indigo-800 mb-4">
                Common Relationship Patterns
              </h3>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <h4 className="text-xl font-semibold text-indigo-700 mb-3">
                    Healthy Patterns
                  </h4>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">•</span>
                      <span className="text-gray-700">
                        Addressing issues promptly rather than letting them
                        fester
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">•</span>
                      <span className="text-gray-700">
                        Taking turns compromising rather than one person always
                        giving in
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">•</span>
                      <span className="text-gray-700">
                        Expressing appreciation more often than criticism
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">•</span>
                      <span className="text-gray-700">
                        Checking in with each other emotionally on a regular
                        basis
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">•</span>
                      <span className="text-gray-700">
                        Balancing giving and receiving in the relationship
                      </span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <h4 className="text-xl font-semibold text-indigo-700 mb-3">
                    Unhealthy Patterns
                  </h4>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <span className="text-red-600 mr-2">•</span>
                      <span className="text-gray-700">
                        Keeping score of past mistakes and bringing them up
                        repeatedly
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-600 mr-2">•</span>
                      <span className="text-gray-700">
                        Using the silent treatment as punishment
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-600 mr-2">•</span>
                      <span className="text-gray-700">
                        Threatening the relationship during arguments ("Maybe we
                        should break up")
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-600 mr-2">•</span>
                      <span className="text-gray-700">
                        Avoiding difficult conversations until they become
                        crises
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-600 mr-2">•</span>
                      <span className="text-gray-700">
                        Prioritizing being right over understanding each other
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Red Flags Section */}
        {activeSection === "redFlags" && (
          <div className="space-y-8">
            <p className="text-xl text-gray-700 mb-8">
              While no relationship is perfect, certain behaviors indicate
              unhealthy dynamics that may become harmful over time. Recognizing
              these red flags early can help you make informed decisions about
              your relationships.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              {redFlags.map((flag, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl shadow-md overflow-hidden border-l-4 border-red-500">
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-red-700 mb-3">
                      {flag.title}
                    </h3>
                    <p className="text-gray-700">{flag.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-red-50 p-8 rounded-2xl border border-red-100 mt-8">
              <div className="flex items-start space-x-4">
                <div className="bg-red-600 p-3 rounded-full text-white">
                  <AlertCircle size={24} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-red-700 mb-2">
                    When to Seek Help
                  </h3>
                  <p className="text-gray-700 mb-4">
                    If you recognize several red flags in your relationship,
                    especially if they're becoming more frequent or intense, it
                    may be time to seek professional support. Remember that
                    healthy relationships make you feel better about yourself,
                    not worse.
                  </p>
                  <p className="text-gray-700">
                    Consider reaching out to a licensed therapist, counselor, or
                    trusted resource if you experience persistent unhealthy
                    patterns or if you feel unsafe in your relationship.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Building Tips Section */}
        {activeSection === "building" && (
          <div className="space-y-8">
            <p className="text-xl text-gray-700 mb-8">
              Healthy relationships require ongoing attention and care. Here are
              practical strategies to strengthen your relationship and nurture
              the qualities that contribute to lasting happiness together.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300">
                <div className="p-3 bg-indigo-100 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <MessageSquare className="text-indigo-600" size={24} />
                </div>
                <h3 className="text-xl font-semibold text-indigo-800 mb-3">
                  Practice Active Listening
                </h3>
                <p className="text-gray-700">
                  Focus fully on your partner when they're speaking. Put away
                  distractions, maintain eye contact, and listen to understand
                  rather than to formulate your response. Reflect back what
                  you've heard to ensure you've understood correctly.
                </p>
                <div className="mt-4 bg-indigo-50 p-3 rounded-lg">
                  <p className="text-sm text-indigo-700 font-medium">
                    Try This: Set aside 15 minutes daily for uninterrupted
                    conversation about your day.
                  </p>
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300">
                <div className="p-3 bg-indigo-100 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <Heart className="text-indigo-600" size={24} />
                </div>
                <h3 className="text-xl font-semibold text-indigo-800 mb-3">
                  Learn Love Languages
                </h3>
                <p className="text-gray-700">
                  Discover how you and your partner prefer to give and receive
                  love—whether through words of affirmation, acts of service,
                  receiving gifts, quality time, or physical touch—and make
                  efforts to express love in ways that resonate most with each
                  other.
                </p>
                <div className="mt-4 bg-indigo-50 p-3 rounded-lg">
                  <p className="text-sm text-indigo-700 font-medium">
                    Try This: Take an online love language quiz together and
                    discuss the results.
                  </p>
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300">
                <div className="p-3 bg-indigo-100 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <Shield className="text-indigo-600" size={24} />
                </div>
                <h3 className="text-xl font-semibold text-indigo-800 mb-3">
                  Create Relationship Rituals
                </h3>
                <p className="text-gray-700">
                  Establish regular rituals that strengthen your bond—whether
                  it's a weekly date night, morning coffee together, or an
                  annual tradition that's meaningful to both of you. These
                  rituals create stability and opportunities for connection.
                </p>
                <div className="mt-4 bg-indigo-50 p-3 rounded-lg">
                  <p className="text-sm text-indigo-700 font-medium">
                    Try This: Create a monthly "adventure day" where you take
                    turns planning an activity.
                  </p>
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300">
                <div className="p-3 bg-indigo-100 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <CheckCircle className="text-indigo-600" size={24} />
                </div>
                <h3 className="text-xl font-semibold text-indigo-800 mb-3">
                  Practice Appreciation
                </h3>
                <p className="text-gray-700">
                  Make a habit of noticing and expressing gratitude for the
                  things your partner does, both big and small. Appreciation
                  counteracts negativity and helps both partners feel valued and
                  recognized.
                </p>
                <div className="mt-4 bg-indigo-50 p-3 rounded-lg">
                  <p className="text-sm text-indigo-700 font-medium">
                    Try This: Share three things you appreciate about each other
                    before bed each night.
                  </p>
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300">
                <div className="p-3 bg-indigo-100 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <Users className="text-indigo-600" size={24} />
                </div>
                <h3 className="text-xl font-semibold text-indigo-800 mb-3">
                  Support Individual Growth
                </h3>
                <p className="text-gray-700">
                  Encourage your partner's personal goals and interests, even
                  when they don't involve you. Celebrate their achievements and
                  provide emotional support during challenges. Make space for
                  individual identity alongside your couple identity.
                </p>
                <div className="mt-4 bg-indigo-50 p-3 rounded-lg">
                  <p className="text-sm text-indigo-700 font-medium">
                    Try This: Set personal goals together, then check in monthly
                    on progress.
                  </p>
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300">
                <div className="p-3 bg-indigo-100 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <Book className="text-indigo-600" size={24} />
                </div>
                <h3 className="text-xl font-semibold text-indigo-800 mb-3">
                  Regular Check-ins
                </h3>
                <p className="text-gray-700">
                  Schedule periodic relationship check-ins where you can discuss
                  what's working well and areas for improvement. Approach these
                  conversations with curiosity and a growth mindset rather than
                  criticism.
                </p>
                <div className="mt-4 bg-indigo-50 p-3 rounded-lg">
                  <p className="text-sm text-indigo-700 font-medium">
                    Try This: Use prompts like "I appreciate when you..." and "I
                    would love if we could..."
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-indigo-600 text-white p-8 rounded-2xl mt-8">
              <h3 className="text-2xl font-bold mb-4">Remember</h3>
              <p className="text-lg">
                Healthy relationships require consistent effort from both
                partners, but that effort should feel rewarding rather than
                depleting. The goal isn't perfection but growth—learning to love
                each other better over time. Small, consistent actions often
                have a greater impact than grand gestures.
              </p>
            </div>
          </div>
        )}

        {/* Bottom Ad */}
        <div className="mt-12" style={{ width: "100%", minWidth: "300px" }}>
          <ins
            className="adsbygoogle"
            style={{ display: "block" }}
            data-ad-client="ca-pub-2067522743161930"
            data-ad-slot="YYYYYYYYYY"
            data-ad-format="auto"
            data-full-width-responsive="true"></ins>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default HealthyRelationshipSigns;
